import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PageTracker } from "@/hooks/usePageTracking";
import { LanguageProvider } from "@/contexts/LanguageContext";
import LanguageRedirect from "@/components/public/LanguageRedirect";
import { lazy, Suspense } from "react";

// Critical components - keep synchronous for fast initial load
import PublicLayout from "./components/public/PublicLayout";
import Home from "./pages/public/Home";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Lazy loaded public pages
const About = lazy(() => import("./pages/public/About"));
const Contact = lazy(() => import("./pages/public/Contact"));
const Blog = lazy(() => import("./pages/public/Blog"));
const BlogPost = lazy(() => import("./pages/public/BlogPost"));
const BlogSubmit = lazy(() => import("./pages/public/BlogSubmit"));
const Videos = lazy(() => import("./pages/public/Videos"));
const PublicPortfolio = lazy(() => import("./pages/public/Projects"));
const FAQ = lazy(() => import("./pages/public/FAQ"));
const Privacy = lazy(() => import("./pages/public/Privacy"));
const Terms = lazy(() => import("./pages/public/Terms"));
const DocumentView = lazy(() => import("./pages/public/DocumentView"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy loaded dashboard pages
const Index = lazy(() => import("./pages/Index"));
const Clients = lazy(() => import("./pages/Clients"));
const ClientDetails = lazy(() => import("./pages/ClientDetails"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetails = lazy(() => import("./pages/ProjectDetails"));
const Finance = lazy(() => import("./pages/Finance"));
const Quotations = lazy(() => import("./pages/finance/Quotations"));
const Invoices = lazy(() => import("./pages/finance/Invoices"));
const Contracts = lazy(() => import("./pages/finance/Contracts"));
const MaintenanceContracts = lazy(() => import("./pages/finance/MaintenanceContracts"));
const Payments = lazy(() => import("./pages/finance/Payments"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const Users = lazy(() => import("./pages/Users"));
const ExpensesPage = lazy(() => import("./pages/ExpensesPage"));
const PartnershipPage = lazy(() => import("./pages/PartnershipPage"));
const RemindersPage = lazy(() => import("./pages/RemindersPage"));
const Domains = lazy(() => import("./pages/Domains"));
const HostingPage = lazy(() => import("./pages/Hosting"));
const ContactRequests = lazy(() => import("./pages/ContactRequests"));
const BlogManagement = lazy(() => import("./pages/BlogManagement"));
const BlogEditor = lazy(() => import("./pages/BlogEditor"));
const ChatbotConversations = lazy(() => import("./pages/ChatbotConversations"));

const queryClient = new QueryClient();

// Wrapper: binds LanguageContext + PublicLayout around a block of public routes.
const PublicShell = () => (
  <LanguageProvider>
    <PublicLayout>
      <Outlet />
    </PublicLayout>
  </LanguageProvider>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <PageTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Root + any non-prefixed public path → redirect to detected language */}
              <Route path="/" element={<LanguageRedirect />} />

              {/* Legacy unprefixed public paths redirect to the lang-prefixed version */}
              <Route path="/about" element={<LanguageRedirect />} />
              <Route path="/contact" element={<LanguageRedirect />} />
              <Route path="/blog" element={<LanguageRedirect />} />
              <Route path="/blog/submit" element={<LanguageRedirect />} />
              <Route path="/blog/:slug" element={<LanguageRedirect />} />
              <Route path="/videos" element={<LanguageRedirect />} />
              <Route path="/portfolio" element={<LanguageRedirect />} />
              <Route path="/faq" element={<LanguageRedirect />} />
              <Route path="/privacy" element={<LanguageRedirect />} />
              <Route path="/terms" element={<LanguageRedirect />} />
              <Route path="/doc/:type/:id" element={<LanguageRedirect />} />

              {/* Auth pages — keep unprefixed Arabic-only for now */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Language-aware public pages */}
              <Route path="/:lang" element={<PublicShell />}>
                <Route index element={<Home />} />
                <Route path="about" element={<About />} />
                <Route path="contact" element={<Contact />} />
                <Route path="blog" element={<Blog />} />
                <Route path="blog/submit" element={<BlogSubmit />} />
                <Route path="blog/:slug" element={<BlogPost />} />
                <Route path="videos" element={<Videos />} />
                <Route path="portfolio" element={<PublicPortfolio />} />
                <Route path="faq" element={<FAQ />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="terms" element={<Terms />} />
                <Route path="doc/:type/:id" element={<DocumentView />} />
              </Route>

              {/* Protected Routes - All Users */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Index />
                </ProtectedRoute>
              } />
              <Route path="/projects" element={
                <ProtectedRoute>
                  <Projects />
                </ProtectedRoute>
              } />
              <Route path="/projects/:id" element={
                <ProtectedRoute>
                  <ProjectDetails />
                </ProtectedRoute>
              } />

              {/* Protected Routes - Management Only (Admin + Partner) */}
              <Route path="/clients" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Clients />
                </ProtectedRoute>
              } />
              <Route path="/clients/:id" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <ClientDetails />
                </ProtectedRoute>
              } />
              <Route path="/domains" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Domains />
                </ProtectedRoute>
              } />
              <Route path="/hosting" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <HostingPage />
                </ProtectedRoute>
              } />
              <Route path="/contacts" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <ContactRequests />
                </ProtectedRoute>
              } />
              <Route path="/finance" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Finance />
                </ProtectedRoute>
              } />
              <Route path="/finance/quotations" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Quotations />
                </ProtectedRoute>
              } />
              <Route path="/finance/invoices" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Invoices />
                </ProtectedRoute>
              } />
              <Route path="/finance/contracts" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Contracts />
                </ProtectedRoute>
              } />
              <Route path="/finance/maintenance" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <MaintenanceContracts />
                </ProtectedRoute>
              } />
              <Route path="/finance/payments" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Payments />
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Reports />
                </ProtectedRoute>
              } />
              <Route path="/partnership" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <PartnershipPage />
                </ProtectedRoute>
              } />
              <Route path="/expenses" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <ExpensesPage />
                </ProtectedRoute>
              } />
              <Route path="/reminders" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <RemindersPage />
                </ProtectedRoute>
              } />

              {/* Protected Routes - Admin Only */}
              <Route path="/users" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Users />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/blog-admin" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <BlogManagement />
                </ProtectedRoute>
              } />
              <Route path="/blog-admin/new" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <BlogEditor />
                </ProtectedRoute>
              } />
              <Route path="/blog-admin/edit/:id" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <BlogEditor />
                </ProtectedRoute>
              } />
              <Route path="/chatbot" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ChatbotConversations />
                </ProtectedRoute>
              } />

              {/* Catch-all Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
